// color-exempt-file — retokenisation 5728fe509 : classes dark:bg-[#hex] ajoutees pour preserver le rendu dark historique (voir memoire ui-themes)
import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toIntlLocale } from "@core/lib/date-utils";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Settings,
  BarChart3,
  Users,
  Calendar,
  Clock,
  Check,
  X,
  HelpCircle,
  Table,
  LayoutGrid,
  Trash2,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@core/components/ui/alert-dialog";

// Import components
import VoterCompletionScreen from "./VoterCompletionScreen";
import VoterForm from "@core/components/voting/VoterForm";
import PollHeader from "@core/components/voting/PollHeader";
import VoteOptionDesktop from "@core/components/voting/VoteOptionDesktop";
import VoteOptionMobile from "@core/components/voting/VoteOptionMobile";
import SlotVoteRow from "@core/components/voting/SlotVoteRow";
import { InviteGate } from "@core/components/voting/InviteGate";
import { useInviteAccess } from "@core/components/voting/hooks/useInviteAccess";
import { voteErrorKey } from "@core/lib/voteErrorMessage";
import { resolveRequireAuth } from "@core/lib/settingsNormalize";
import { useFeatureFlags } from "@core/contexts/FeatureFlagsContext";
// Composant unifié pour les deux expériences de vote (tableau et swipe)

// Import types and utilities
import { logger } from "@core/lib/logger";
import { isDev } from "@core/lib/env";
import { getGuestIdentity, getDeviceId } from "@core/lib/guestIdentityService";
import {
  VoterInfo,
  FormErrors,
  VoteType,
} from "@core/components/voting/utils/types";

// Import custom hooks
// ✅ CORRECT: Using unified useVoting (fingerprint version) from components/voting/hooks
import { useVoting } from "@core/components/voting/hooks";
import { useIsDesktop } from "@core/components/voting/hooks";
import { usePollBySlug } from "@core/hooks/usePoll";
import { readableTextColor } from "@core/lib/templateContrast";
import { usePollVotes } from "@core/hooks/usePollVotes";
import { formatDate } from "@core/components/voting/utils/dateUtils";
import { supabase } from "@core/lib/supabase";
import { deletePollResponse } from "@core/lib/edgeFunctionClient";
import { useUserData } from "@core/hooks/useUserData";
import { useAuth } from "@core/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "@core/hooks/use-toast";
import { useVotingViewMode } from "@core/hooks/useVotingViewMode";
import { buildPollOptions } from "@core/lib/buildPollOptions";
import {
  countUniqueVoters,
  resolveVoterName,
} from "@core/lib/countUniqueVoters";
import { useRecurringSeries } from "@core/hooks/useRecurringSeries";
import { nextOccurrence } from "@core/lib/recurrence";
import { Repeat } from "lucide-react";
import FinalDateSubscribe from "@core/components/final-date/FinalDateSubscribe";
import { useFinalDateAvailable } from "@core/hooks/useFinalDateAvailable";

// Hook pour forcer la génération du fingerprint même si le poll n'existe pas
const useForceFingerprint = () => {
  React.useEffect(() => {
    // Forcer la génération du fingerprint au montage du composant
    const initFingerprint = async () => {
      try {
        const identity = await getGuestIdentity();
        console.log(
          "🔍 ForceFingerprint - Identity generated:",
          identity.fingerprint.substring(0, 16) + "...",
        );
      } catch (error) {
        console.error("🔍 ForceFingerprint - Error:", error);
      }
    };

    initFingerprint();
  }, []); // Exécuté une seule fois au montage
};

// 🔄 MIGRATION NOTE:
// Previously used: @core/hooks/useVoting (archived - name/email version)
// Now using: @core/components/voting/hooks/useVoting (unified - fingerprint version)
//
// Key changes for Voting:
// - No more voterInfo.name/email state management
// - Fingerprint-based user recognition (automatic)
// - submitVote() signature: submitVote(optionId, voteType) - no name/email needed
// - User identity handled automatically by getGuestIdentity() in the hook

interface VotingProps {
  pollId: string;
  pollType?: "date";
  showResults?: boolean;
  onBack?: () => void;
  onVoteSubmitted?: () => void;
  onViewResults?: () => void;
  slug?: string;
}

/**
 * Voting - Composant unifié pour les deux expériences de vote (tableau et swipe)
 * Architecture simple et épurée sans adaptateur complex
 */
const Voting: React.FC<VotingProps> = ({
  pollId,
  pollType = "date",
  showResults = false,
  onBack,
  onVoteSubmitted,
  onViewResults,
  slug,
}) => {
  const { t, i18n } = useTranslation(["polls", "common", "errors"]);

  // Forcer la génération du fingerprint dès le montage du composant
  useForceFingerprint();

  // Détecter si on est sur desktop
  const isDesktop = useIsDesktop();
  const { flags } = useFeatureFlags();
  const redesignV2 = flags.enableRedesignPollsV2;
  const navigate = useNavigate(); // Initialized useNavigate
  const userData = useUserData();
  const { user, profile } = useAuth();
  const { toast } = useToast();

  // Gestion du mode de vue (swipe vs table)
  const { viewMode, toggleViewMode, isTableMode } = useVotingViewMode({
    pollId: pollId,
  });

  // Récupérer le vrai poll depuis usePollBySlug (comme dans la version originale)
  const { data: realPoll, isLoading, error } = usePollBySlug(slug);

  // Récupérer les votes RÉELS depuis Supabase
  const {
    votes: allVotes,
    getStats: getRealStats,
    getRanking: getRealRanking,
  } = usePollVotes(realPoll?.id);

  // Nombre de votants uniques — source unique avec Results via countUniqueVoters.
  const totalVoters = React.useMemo(
    () => countUniqueVoters(allVotes as any),
    [allVotes],
  );

  // Transformer directement le UnifiedPoll en options avec créneaux horaires.
  // Source unique avec Results via buildPollOptions (packages/core/src/lib).
  const options = React.useMemo(() => {
    if (!realPoll) return { options: [], allowMaybeVotes: true };
    const settings = (realPoll as any).settings || {};
    const allowMaybeVotes = settings.allowMaybeVotes !== false;
    return { options: buildPollOptions(realPoll), allowMaybeVotes };
  }, [realPoll]);

  const { options: pollOptions, allowMaybeVotes } = options;

  // ---- Settings avancés appliqués côté vote (spec settings-enforcement) ----
  // Le serveur (RPC submit/delete, migration 20260723090000) reste la source
  // de vérité ; ces gates client évitent de laisser saisir un vote condamné.
  const pollSettings = React.useMemo(
    () => ((realPoll as any)?.settings || {}) as Record<string, unknown>,
    [realPoll],
  );
  // Lecture tolérante : un sondage enregistré avant le 2026-08-16 peut porter
  // `allowAnonymousVotes: false` au lieu de `requireAuth: true` — même
  // intention, clé différente. resolveRequireAuth accepte les deux.
  const requireAuthSetting = resolveRequireAuth(pollSettings);
  const editLocked =
    pollSettings.allowEditAfterSubmit === false ||
    pollSettings.allowEditAfterSubmit === "false";
  const maxResponsesSetting = React.useMemo(() => {
    const n = Number(pollSettings.maxResponses);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [pollSettings.maxResponses]);
  const resultsHidden =
    (pollSettings.resultsVisibility === "creator-only" ||
      pollSettings.resultsVisibility === "voters") &&
    !(user?.id && user.id === (realPoll as any)?.creator_id);

  // Bouton résultats visible si: résultats publics/votants OU créateur
  // Serveur gatekeeper l'accès réel (has_voted check pour "voters")
  const resultsButtonVisible =
    pollSettings.resultsVisibility === "public" ||
    pollSettings.resultsVisibility === "everyone" ||
    pollSettings.resultsVisibility === "all" ||
    pollSettings.resultsVisibility === "voters" ||
    (user?.id && user.id === (realPoll as any)?.creator_id);

  // Compteur serveur (RPC get_poll_response_count, migration 20260724090000) :
  // indispensable au gate « Sondage complet » quand resultsVisibility masque
  // get_poll_responses (allVotes vide → totalVoters=0 côté client).
  const [serverResponseCount, setServerResponseCount] = React.useState<
    number | null
  >(null);
  React.useEffect(() => {
    if (!realPoll?.id || maxResponsesSetting == null) return;
    supabase
      .rpc("get_poll_response_count", { p_poll_id: realPoll.id })
      .then(({ data, error: countError }) => {
        if (!countError && typeof data === "number") {
          setServerResponseCount(data);
        }
      });
  }, [realPoll?.id, maxResponsesSetting]);

  // ---- Sondage récurrent : occurrences annulées masquées + vue focus ----
  // La série (si elle existe) fournit le statut de chaque occurrence. Les
  // dates annulées restent dans selectedDates (les index des votes ne doivent
  // jamais glisser) mais sont filtrées de l'affichage ici.
  const { data: recurringSeries } = useRecurringSeries(realPoll?.id);
  const [recurringViewAll, setRecurringViewAll] = useState<boolean>(
    () => new URLSearchParams(window.location.search).get("view") === "all",
  );
  // ?focus_instance=<id> (lien de rappel email) : pré-cible cette occurrence.
  const focusInstanceId = React.useMemo(
    () => new URLSearchParams(window.location.search).get("focus_instance"),
    [],
  );

  const recurring = React.useMemo(() => {
    if (!recurringSeries || !recurringSeries.instances?.length) return null;
    const cancelled = new Set(
      recurringSeries.instances
        .filter((i) => i.status === "cancelled")
        .map((i) => i.occurrence_date),
    );
    const today = new Date().toISOString().slice(0, 10);
    const focusedInstance = focusInstanceId
      ? recurringSeries.instances.find(
          (i) => i.id === focusInstanceId && i.status === "active",
        )
      : null;
    const target =
      focusedInstance ?? nextOccurrence(recurringSeries.instances, today);
    return {
      cancelled,
      focusDate: target?.occurrence_date ?? null,
      focusPosition:
        recurringSeries.instances.find(
          (i) => i.occurrence_date === target?.occurrence_date,
        )?.position ?? null,
      total: recurringSeries.instances.length,
    };
  }, [recurringSeries, focusInstanceId]);

  // Options affichées : jamais les dates annulées ; en vue focus, uniquement
  // la prochaine occurrence (ou celle du lien de rappel).
  const displayOptions = React.useMemo(() => {
    if (!recurring) return pollOptions;
    const notCancelled = pollOptions.filter(
      (o) => !recurring.cancelled.has(o.option_date),
    );
    if (recurringViewAll || !recurring.focusDate) return notCancelled;
    const focusedOnly = notCancelled.filter(
      (o) => o.option_date === recurring.focusDate,
    );
    // Série terminée ou date focus sans option : retomber sur la vue complète.
    return focusedOnly.length > 0 ? focusedOnly : notCancelled;
  }, [pollOptions, recurring, recurringViewAll]);

  // États locaux simples pour le vote USER (ce que l'utilisateur est en train de faire)
  const [votes, setVotes] = React.useState<
    Record<string, "yes" | "no" | "maybe">
  >({});
  const [showForm, setShowForm] = useState(false);
  const [showVotedMessage, setShowVotedMessage] = useState(true);
  const [isVoteComplete, setIsVoteComplete] = useState(false);
  // ID de la ligne poll_responses créée par le vote courant. Sert à renvoyer
  // une copie de confirmation : le destinataire est dérivé server-side depuis
  // cette ligne (#810), jamais d'une adresse libre saisie après coup.
  const [votedResponseId, setVotedResponseId] = useState<string | null>(null);
  const [voterName, setVoterName] = useState("");
  const [currentUserDisplayName, setCurrentUserDisplayName] = useState(() =>
    t("polls:you"),
  );
  const [hasAlreadyVoted, setHasAlreadyVoted] = useState(false);
  const [checkingVote, setCheckingVote] = useState(false);
  const [showSelfDeleteDialog, setShowSelfDeleteDialog] = useState(false);
  const [isDeletingOwnVote, setIsDeletingOwnVote] = useState(false);

  // 🔄 MIGRATION: Plus besoin de voterInfo avec le nouveau hook
  // L'identité utilisateur est gérée automatiquement par fingerprint dans useVoting
  // const voterInfo = voterName ? { name: voterName } : undefined; // ❌ ARCHIVED

  // Accès « sur invitation ». Rien n'est décidé ici : l'ancien gate comparait
  // l'adresse saisie à une liste envoyée au navigateur (contournable, et la
  // liste des invités fuitait vers tout visiteur du lien). Le verdict vient
  // désormais de la RPC check_poll_invitation, et c'est de toute façon
  // submit_poll_response qui tranche à l'écriture.
  //
  // Le préflight lui-même vit dans `useInviteAccess` (packages/core) depuis
  // qu'il sert aussi aux questionnaires : il n'a jamais rien eu de spécifique
  // aux sondages de dates.
  const [voterEmail, setVoterEmail] = useState<string>("");
  // E-mail saisi au moment du vote : préremplit « Préviens-moi quand la date
  // est fixée » sur l'écran de fin (la case, elle, reste non cochée).
  const [submittedEmail, setSubmittedEmail] = useState<string>("");
  const finalDateAvailable = useFinalDateAvailable();

  const { isInviteOnly, inviteReason, inviteEmailMasked, isCheckingAccess } =
    useInviteAccess(realPoll?.id, (realPoll as any)?.settings);

  const normalizeIdentity = (value?: string | null) =>
    (value || "").trim().toLowerCase();

  const buildParticipantKey = (identity: {
    email?: string | null;
    stableFingerprint?: string | null;
    deviceId?: string | null;
    name?: string | null;
  }) => {
    const emailKey = normalizeIdentity(identity.email);
    if (emailKey) return `email:${emailKey}`;

    // stable_fingerprint : invariant entre navigateurs sur la même machine,
    // empêche le bypass de la limite participants en changeant de navigateur.
    const stableKey = normalizeIdentity(identity.stableFingerprint);
    if (stableKey) return `stable:${stableKey}`;

    const deviceKey = normalizeIdentity(identity.deviceId);
    if (deviceKey) return `device:${deviceKey}`;

    const nameKey = normalizeIdentity(identity.name);
    if (nameKey) return `name:${nameKey}`;

    return null;
  };

  // Logique de vote simple synchronisée avec useVoting
  const updateVote = (optionId: string, voteType: "yes" | "no" | "maybe") => {
    logger.info("VOTING - User vote updated", "vote", {
      optionId,
      voteType,
      pollId: pollIdForVoting,
      totalVotes: Object.keys({ ...votes, [optionId]: voteType }).length,
    });

    setVotes((prev) => ({ ...prev, [optionId]: voteType }));
    // Synchroniser avec useVoting
    if (votingHook && votingHook.updateVote) {
      votingHook.updateVote(optionId, voteType);
    }
  };

  // Vote du user actuel persistant côté serveur (clé = optionId → "yes"/"no"/"maybe")
  // dérivé de allVotes filtré par device_id. Sert au calcul optimiste des stats.
  const userSavedVotes = React.useMemo(() => {
    const myDeviceId = userData.deviceId;
    if (!myDeviceId) return {} as Record<string, "yes" | "no" | "maybe">;
    const myRow = allVotes.find(
      (v) => (v as any).is_own_response === true,
    ) as any;
    if (!myRow) return {} as Record<string, "yes" | "no" | "maybe">;
    const rd = (myRow.response_data || {}) as Record<string, unknown>;
    const out: Record<string, "yes" | "no" | "maybe"> = {};
    for (const [k, v] of Object.entries(rd)) {
      if (k === "voterName") continue;
      if (v === "yes" || v === "no" || v === "maybe") out[k] = v;
    }
    return out;
  }, [allVotes, userData.deviceId]);

  // Toggle : reclic sur la même valeur retire le vote (cf. UX validée par PO).
  const toggleVote = (optionId: string, value: "yes" | "no" | "maybe") => {
    if (votingHook && votingHook.currentVote?.[optionId] === value) {
      votingHook.removeVote(optionId);
    } else if (votingHook && votingHook.updateLocalVote) {
      votingHook.updateLocalVote(optionId, value);
    }
  };

  const handleVote = (optionId: string, voteType: "yes" | "no" | "maybe") => {
    toggleVote(optionId, voteType);
  };

  // Utiliser useVoting pour la persistance et la reconnaissance par fingerprint
  // Utiliser pollId de l'URL si realPoll n'est pas encore chargé
  const pollIdForVoting = realPoll?.id || slug || "";

  const votingHook = useVoting(pollIdForVoting);

  // Fix #721 — initialise currentUserDisplayName depuis auth.user au mount,
  // sans attendre userHasVoted. Cela garantit que le filtre dedup par nom
  // (seen.add) fonctionne dès le premier rendu et évite la double ligne
  // "Vous" + "<nom>" dans la table des votants.
  React.useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const authName =
        data?.user?.user_metadata?.full_name ||
        data?.user?.user_metadata?.name ||
        data?.user?.email;
      if (authName) {
        setCurrentUserDisplayName((prev) =>
          !prev || prev === t("polls:you") ? authName : prev,
        );
        logger.info("VOTING - Auth display name initialised at mount", "vote", {
          authName,
        });
      }
    });
  }, []); // mount uniquement

  // Effet pour charger le nom d'affichage de l'utilisateur actuel
  React.useEffect(() => {
    const loadCurrentUserDisplayName = async () => {
      if (votingHook.userHasVoted) {
        try {
          const displayName = await votingHook.getCurrentUserDisplayName();
          setCurrentUserDisplayName(displayName);
          logger.info("VOTING - Current user display name loaded", "vote", {
            pollId: pollIdForVoting,
            displayName,
          });
        } catch (error) {
          logger.error(
            "VOTING - Error loading current user display name",
            "vote",
            { error },
          );
        }
      }
    };

    loadCurrentUserDisplayName();
  }, [votingHook.userHasVoted]);

  // Bug #3 fix : préfille le state local `voterName` avec le full_name du
  // profil dès qu'il est disponible, pour que l'écran de complétion et la
  // ligne de résultat affichent le nom saisi à l'inscription, pas l'email.
  // L'utilisateur reste libre d'éditer (formulaire invité) — on n'écrase
  // pas un nom déjà saisi manuellement.
  React.useEffect(() => {
    if (voterName) return;
    const authenticatedName =
      profile?.full_name?.trim() ||
      (user?.user_metadata?.full_name as string | undefined)?.trim() ||
      "";
    if (authenticatedName) setVoterName(authenticatedName);
  }, [profile?.full_name, user?.user_metadata]);

  // Option B : si l'utilisateur vient de se logger et a des votes anon sur ce
  // poll avec un voterName différent de son nom de profil, on met à jour le
  // voterName pour cohérence visuelle. On garde respondent_name = fingerprint
  // (pas de transfert d'identité en BDD — ownership reste ancrée au fingerprint).
  React.useEffect(() => {
    const syncVoterNameFromProfile = async () => {
      if (!user || !profile?.full_name || !pollId) return;
      try {
        const identity = await getGuestIdentity();
        const fullName = profile.full_name.trim();

        // Bug hunt Nuit 2 (2026-05-05) — S2 : get_my_poll_response renvoie
        // uniquement la ligne de l'appelant (résout fingerprint côté serveur).
        const { data: myResponses, error: fetchError } = await supabase.rpc(
          "get_my_poll_response",
          {
            p_poll_id: pollId,
            p_fingerprint: identity.stableFingerprint || null,
          },
        );

        if (fetchError) return;
        const existing = (myResponses || [])[0];
        if (!existing) return;

        const existingVoterName = existing.response_data?.voterName;
        if (!existingVoterName || existingVoterName === fullName) return;

        const updatedResponseData = {
          ...existing.response_data,
          voterName: fullName,
        };

        // Bug hunt Nuit 2 (2026-05-05) — S1 : migré vers submit_poll_response.
        const { error: updateError } = await supabase.rpc(
          "submit_poll_response",
          {
            p_poll_id: pollId,
            p_response_data: existing.response_data,
            p_voter_name: fullName,
            p_response_type: "date",
            p_fingerprint: identity.stableFingerprint || null,
          },
        );

        if (updateError) {
          logger.warn(
            "VOTING - Failed to sync voterName from profile",
            "vote",
            {
              error: updateError.message,
            },
          );
          return;
        }

        logger.info(
          "VOTING - voterName synced from authenticated profile",
          "vote",
          {
            pollId,
            previousName: existingVoterName,
            newName: fullName,
          },
        );
      } catch (error) {
        logger.debug(
          "VOTING - syncVoterNameFromProfile silent failure",
          "vote",
          {
            error: error instanceof Error ? error.message : String(error),
          },
        );
      }
    };

    syncVoterNameFromProfile();
  }, [user?.id, profile?.full_name, pollId]);

  // 🔄 MIGRATION: Wrapper pour VotingTableNew mis à jour
  // Ancienne signature: handleTableNewVote(voterName, optionId, value)
  // Nouvelle signature: handleTableNewVote(optionId, value) - voterName géré par fingerprint
  const handleTableNewVote = async (
    optionId: string, // voterName supprimé - géré automatiquement
    value: "yes" | "no" | "maybe",
  ) => {
    // ð³ INTERFACE SEULEMENT - PAS DE SAUVEGARDE AUTOMATIQUE
    // Les clics sur les boutons ne font que changer la couleur, pas de sauvegarde en base

    // Mettre à jour l'état local uniquement
    if (votingHook && votingHook.updateLocalVote) {
      votingHook.updateLocalVote(optionId, value);
    }
  };

  // Bug hunt 2026-04-23 — bug #5 : ne plus incrémenter optimistiquement le
  // compteur par option avant soumission. Le header "N votant" lit `allVotes`
  // (serveur pur) et n'incluait pas le vote en cours, d'où un "1 0 1 (2)" à
  // côté d'un "0 votant". On fige donc les stats par option sur l'état serveur
  // tant que le vote n'est pas soumis.
  // Stats optimistes : serveur - vote sauvé du user + vote courant du user.
  // Cohérent en card view (VoteOption*) et table view (tfoot RÉSULTATS).
  const getStatsWithUser = (optionId: string) => {
    const base = getRealStats(optionId);
    const result = { yes: base.yes, no: base.no, maybe: base.maybe };
    // userSavedVotes dépend de is_own_response (match device_id) — vide pour
    // les votes soumis par RPC (device_id NULL). Fallback : le vote persisté
    // résolu par get_my_poll_response (fingerprint), sinon le vote courant
    // d'un revenant est compté EN PLUS de sa ligne serveur (double comptage).
    const saved = userSavedVotes[optionId] ?? votingHook?.savedVote?.[optionId];
    const current = votingHook?.currentVote?.[optionId];
    if (saved && saved !== current) {
      result[saved] = Math.max(0, result[saved] - 1);
    }
    if (current && current !== saved) {
      result[current] = (result[current] || 0) + 1;
    }
    return result;
  };

  // Stats existantes (sans le vote courant de l'utilisateur)
  const getExistingStats = (optionId: string) => {
    return getRealStats(optionId);
  };

  // Classement basé sur les votes réels + potentiellement le vote en cours
  // Note: getRealRanking retourne un Record<optionId, rank>
  const getRanking = () => {
    // Idéalement on devrait recalculer le ranking avec le vote utilisateur en cours
    // Pour l'instant on utilise le ranking réel, c'est déjà beaucoup mieux que rien !
    return getRealRanking("all");
  };

  // Progression calculée sur les options AFFICHÉES (vue focus récurrente :
  // un revote partiel ne doit pas afficher >100%).
  const userTotalVotes = displayOptions.filter((o) => votes[o.id]).length;
  const remainingVotes = displayOptions.length - userTotalVotes;
  const progressPercent =
    displayOptions.length > 0
      ? (userTotalVotes / displayOptions.length) * 100
      : 0;

  // Vérifier si l'utilisateur a déjà voté (si l'option est activée)
  React.useEffect(() => {
    if (!realPoll || !userData.deviceId || !pollId) return;

    // Vérifier si on a déjà des votes chargés pour cet utilisateur dans allVotes
    // C'est une optimisation par rapport à l'appel API dédié
    if (allVotes.length > 0) {
      // On peut vérifier ici si userData.deviceId est dans les votes
      // Mais les votes sont parfois anonymisés ou sans device_id exposé publiquement
      // Donc on garde la vérification sécurisée via RPC ou filtre dédié si nécessaire
      // Pour l'instant on garde la logique existante ci-dessous qui est sûre
    }

    const settings = (realPoll as any).settings || {};
    // Si l'option "Une réponse par personne" est activée
    if (settings.oneResponsePerPerson) {
      const checkVote = async () => {
        setCheckingVote(true);
        try {
          logger.info("Vérification vote existant", "vote", {
            pollId,
            deviceId: userData.deviceId,
          });
          // RPC SECURITY DEFINER (lockdown RLS anon, bug hunt 2026-04-23).
          // Filtre device_id côté client.
          const { data: allResponses, error } = await supabase.rpc(
            "get_poll_responses",
            { p_poll_id: pollId, p_device_id: getDeviceId() },
          );
          const count = (allResponses || []).filter(
            (r: any) => r.is_own_response === true,
          ).length;

          if (!error && count > 0) {
            setHasAlreadyVoted(true);
            logger.info("Utilisateur a déjà voté", "vote", {
              pollId,
              deviceId: userData.deviceId,
            });
          }
        } catch (err) {
          logger.error("Erreur vérification vote existant", "vote", {
            error: err,
          });
        } finally {
          setCheckingVote(false);
        }
      };

      checkVote();
    }
  }, [realPoll, userData.deviceId, pollId]); // Supprimé allVotes pour éviter la boucle infinie

  // Formatter la date de fin
  const formatDateFin = (dateString: string) => {
    if (!dateString) return t("polls:no_end_date");
    const date = new Date(dateString);
    return date.toLocaleDateString(toIntlLocale(i18n.language), {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Gérer les états de chargement
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen-safe bg-background dark:bg-[#0a0a0a]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen-safe bg-background dark:bg-[#0a0a0a]">
        <div className="text-center">
          <p className="text-destructive">{t("errors:poll_load_failed")}</p>
          <Link to="/" className="text-primary hover:text-primary/80 mt-2">
            {t("common:back_home")}
          </Link>
        </div>
      </div>
    );
  }

  if (!realPoll) {
    return (
      <div className="flex items-center justify-center min-h-screen-safe bg-background dark:bg-[#0a0a0a]">
        <div className="text-center">
          <p className="text-muted-foreground">{t("polls:poll_not_found")}</p>
          <Link to="/" className="text-primary hover:text-primary/80 mt-2">
            {t("common:back_home")}
          </Link>
        </div>
      </div>
    );
  }

  // Gate "Authentification requise" : bloque AVANT la saisie du vote (le
  // serveur rejette de toute façon avec auth_required).
  if (requireAuthSetting && !user) {
    return (
      <div className="flex flex-col min-h-screen-safe bg-background dark:bg-[#0a0a0a]">
        <PollHeader
          poll={realPoll}
          totalVotes={0}
          remainingVotes={0}
          progressPercent={0}
        />
        <div className="flex-1 flex items-center justify-center px-4">
          <div
            data-testid="require-auth-gate"
            className="bg-card dark:bg-[#1e1e1e] border border-border rounded-lg p-8 text-center max-w-md"
          >
            <div className="text-3xl mb-3" aria-hidden>
              🔒
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">
              {t("polls:vote_auth_required_title")}
            </h2>
            <p className="text-muted-foreground text-sm mb-6">
              {t("polls:vote_auth_required_message")}
            </p>
            <Link
              to={`/auth/signin?redirect=/poll/${slug}`}
              className="inline-block w-full py-3 px-4 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-semibold transition-colors"
            >
              {t("polls:sign_in_to_vote")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Gate "Sondage complet" : maxResponses atteint et ce navigateur n'a pas de
  // réponse existante à modifier.
  if (
    maxResponsesSetting != null &&
    Math.max(totalVoters, serverResponseCount ?? 0) >= maxResponsesSetting &&
    !votingHook.userHasVoted &&
    !hasAlreadyVoted
  ) {
    return (
      <div className="flex flex-col min-h-screen-safe bg-background dark:bg-[#0a0a0a]">
        <PollHeader
          poll={realPoll}
          totalVotes={0}
          remainingVotes={0}
          progressPercent={0}
        />
        <div className="flex-1 flex items-center justify-center px-4">
          <div
            data-testid="poll-full-gate"
            className="bg-card dark:bg-[#1e1e1e] border border-border rounded-lg p-8 text-center max-w-md"
          >
            <div className="text-3xl mb-3" aria-hidden>
              🔢
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">
              {t("polls:poll_full_title")}
            </h2>
            <p className="text-muted-foreground text-sm">
              {t("polls:poll_full_message", { max: maxResponsesSetting })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Sondage sur invitation : le motif du refus vient du serveur.
  if (isInviteOnly && inviteReason && !isCheckingAccess) {
    return (
      <div className="flex flex-col min-h-screen-safe bg-background dark:bg-[#0a0a0a]">
        <PollHeader
          poll={realPoll}
          totalVotes={0}
          remainingVotes={0}
          progressPercent={0}
        />
        <InviteGate
          reason={inviteReason}
          pollTitle={realPoll?.title || t("polls:poll")}
          onSignIn={() => navigate("/auth")}
        />
      </div>
    );
  }

  if (showResults) {
    return (
      <div className="min-h-screen-safe bg-background dark:bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto p-4">
          <div className="bg-card dark:bg-[#1e1e1e] rounded-lg shadow-lg p-6 text-center space-y-6">
            <h2 className="text-2xl font-bold mb-4 text-foreground">
              {t("polls:vote_results_title")}
            </h2>
            <p className="text-muted-foreground">
              {t("polls:results_under_development")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // « Préviens-moi quand la date est fixée » : sondage de dates ouvert, pas
  // encore de date fixée (spec S3).
  const offerFinalDateSubscribe =
    finalDateAvailable &&
    !!realPoll?.id &&
    ((realPoll as any)?.status ?? "active") === "active" &&
    !(realPoll as any)?.finalDate;

  // Si l'utilisateur a déjà voté (détecté au chargement), afficher l'écran de fin
  if (hasAlreadyVoted) {
    return (
      <VoterCompletionScreen
        voterName={userData.userId ? t("polls:member") : t("polls:guest")}
        pollTitle={realPoll?.title}
        pollSlug={slug}
        pollId={realPoll?.id}
        title={t("polls:already_voted_title")}
        message={t("polls:already_voted_message")}
        offerFinalDateSubscribe={offerFinalDateSubscribe}
        onViewResults={() => {
          logger.info("View results clicked (already voted)", "vote");
          navigate(`/poll/${slug}/results`);
        }}
        showCreatePoll
      />
    );
  }

  // Écran de fin après vote réussi
  if (isVoteComplete) {
    return (
      <VoterCompletionScreen
        voterName={voterName || t("polls:default_respondent_name")}
        pollTitle={realPoll?.title}
        pollSlug={slug}
        pollId={realPoll?.id}
        offerFinalDateSubscribe={offerFinalDateSubscribe}
        voterEmail={submittedEmail || voterEmail || undefined}
        onViewResults={() => navigate(`/poll/${slug}/results`)}
        showCreatePoll
      />
    );
  }

  const handleSelfDelete = async () => {
    if (!realPoll?.id) return;
    setIsDeletingOwnVote(true);
    try {
      const identity = await getGuestIdentity();
      const fp = identity.stableFingerprint || null;

      // Récupérer l'ID de la réponse via get_my_poll_response
      const { data: myRows } = await supabase.rpc("get_my_poll_response", {
        p_poll_id: realPoll.id,
        p_fingerprint: fp,
      });
      const myResponse = (myRows || [])[0];
      if (!myResponse) {
        toast({
          title: t("polls:no_vote_found_for_browser"),
          variant: "destructive",
        });
        return;
      }

      // Phase 1 security fix #859: Use Edge Function wrapper instead of direct RPC
      let delError;
      try {
        await deletePollResponse({
          response_id: myResponse.id,
          fingerprint: fp,
        });
        delError = null;
      } catch (err) {
        delError = err instanceof Error ? err : new Error(String(err));
      }
      if (delError) throw delError;

      toast({ title: t("polls:vote_deleted") });
      navigate(`/poll/${slug}`);
    } catch (err) {
      const rawMsg = err instanceof Error ? err.message : "";
      toast({
        title: t("errors:generic"),
        description: rawMsg.includes("edit_forbidden")
          ? t("errors:vote_edit_forbidden")
          : t("errors:vote_delete_failed"),
        variant: "destructive",
      });
    } finally {
      setIsDeletingOwnVote(false);
      setShowSelfDeleteDialog(false);
    }
  };

  const handleFinalizeVote = async (info: {
    name: string;
    email: string;
    wantToReceiveEmail?: boolean;
  }) => {
    setVoterName(info.name);
    // Utiliser l'email autorisé si disponible (pour sondages restreints)
    const finalEmail = voterEmail || info.email;
    setSubmittedEmail(finalEmail || "");

    try {
      // Le pré-contrôle « limite de participants » a été retiré le 2026-08-16.
      //
      // Il plafonnait TOUT sondage de dates à 10 votants. La valeur venait de
      // `settings.maxParticipants` si présente, sinon d'une constante en dur
      // (`DATE_FREE_PARTICIPANTS_LIMIT = 10`) — et c'est cette constante qui
      // s'appliquait en pratique : la clé `maxParticipants` n'était plus
      // écrite par le chemin de création vivant (l'objet littéral de
      // PollCreator ne la contient pas ; `getDefaultSettings`, qui la posait,
      // appartient à un module non branché). Le plafond était donc subi par
      // tous, sans qu'aucun créateur ne puisse ni le choisir ni le voir.
      // Trois raisons de le supprimer plutôt que de le corriger :
      //
      //  1. il contredisait la doctrine tarifaire du dépôt — « participants et
      //     réponses illimités pour tous les plans » (pricing-contract.test.ts) ;
      //  2. il ne garantissait rien : appel client, contournable par un appel
      //     RPC direct, comme le gate email d'avant-hier ;
      //  3. la vraie limite existe et elle est appliquée côté serveur —
      //     `maxResponses`, que les deux RPC vérifient (`poll_full`) et sur
      //     laquelle repose déjà l'écran « Sondage complet » de cette page.
      //
      // Un créateur qui veut une limite la fixe désormais explicitement dans
      // les réglages, et c'est le serveur qui la tient.

      // Utiliser useVoting.currentVote au lieu de votes local
      const allVotes = Object.entries(votingHook.currentVote || {});
      const votesToSubmit = allVotes.filter(
        ([_, voteType]) => voteType !== undefined,
      );

      logger.info("VOTING - Preparing vote submission", "vote", {
        pollId,
        totalVotes: allVotes.length,
        explicitVotes: votesToSubmit.length,
        voteDetails: votesToSubmit.map(([optionId, voteType]) => ({
          optionId,
          voteType,
        })),
        voterName: info.name,
        voterEmail: finalEmail,
        allVotesContent: allVotes,
        explicitVotesContent: votesToSubmit,
      });

      if (votesToSubmit.length === 0) {
        logger.warn("VOTING - No explicit votes to submit", "vote", {
          allVotes: allVotes.map(([optionId, voteType]) => ({
            optionId,
            voteType,
          })),
        });
        throw new Error(t("polls:select_at_least_one_availability"));
      }

      // Soumettre tous les votes en une seule opération atomique
      const votesMap = Object.fromEntries(votesToSubmit) as Record<
        string,
        "yes" | "no" | "maybe"
      >;

      if (!votingHook || !votingHook.submitAllVotes) {
        throw new Error(t("errors:vote_service_unavailable"));
      }

      // Persiste l'email avec le vote uniquement si le votant a opté pour la
      // copie : c'est la source server-side du destinataire de confirmation (#810).
      const optInEmail =
        info.wantToReceiveEmail && finalEmail ? finalEmail : undefined;
      const newResponseId = await votingHook.submitAllVotes(
        votesMap,
        info.name,
        optInEmail,
      );
      setVotedResponseId(newResponseId);

      // Mettre à jour l'état local après sauvegarde réussie
      for (const [optionId, voteType] of votesToSubmit) {
        if (votingHook && votingHook.updateLocalVote) {
          votingHook.updateLocalVote(optionId, voteType);
        }
      }

      setIsVoteComplete(true);
      onVoteSubmitted?.();
      setShowForm(false);

      // Bug-hunt N°13 #3 — envoyer une copie du vote par email si demandé.
      // Le destinataire est dérivé server-side depuis newResponseId (#810).
      if (info.wantToReceiveEmail && optInEmail && newResponseId && realPoll) {
        try {
          const selectedDates = pollOptions
            .filter(
              (opt) =>
                votesMap[opt.id] === "yes" || votesMap[opt.id] === "maybe",
            )
            .map((opt) => ({ date: opt.option_date, slots: [] as string[] }));
          if (selectedDates.length > 0) {
            const { sendDatePollConfirmationEmail } =
              await import("@core/services/EmailService");
            await sendDatePollConfirmationEmail({
              responseId: newResponseId,
              selectedDates,
              respondentName: info.name || t("polls:default_respondent_name"),
            });
          }
        } catch (emailError) {
          logger.error(
            "VOTING - Erreur envoi email confirmation vote",
            "vote",
            { emailError },
          );
        }
      }
    } catch (error) {
      logger.error("Erreur sauvegarde vote", "vote", { error });
      // Mapping des codes d'enforcement serveur (migration 20260723090000)
      // vers des messages traduits — jamais de message technique anglais brut.
      //
      // Passé dans `voteErrorKey` (packages/core) le 2026-08-16 : la chaîne de
      // `if` ne couvrait que trois codes, alors que les sept motifs
      // d'invitation ont leurs clés dans errors.json depuis la veille. Le gate
      // les intercepte presque toujours en amont — presque : une invitation
      // révoquée entre le préflight et l'envoi affichait « invite_revoked ».
      const translatedKey = voteErrorKey(error);
      if (translatedKey) {
        throw new Error(t(translatedKey));
      }
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(t("errors:vote_submission_failed"));
    }
  };

  // Si l'utilisateur est connecté, on dispose déjà de son identité :
  // on finalise le vote sans afficher le formulaire de nom.
  const handleSubmitClick = async () => {
    if (userData.isAuthenticated) {
      const authenticatedName =
        profile?.full_name?.trim() ||
        (user?.user_metadata?.full_name as string | undefined)?.trim() ||
        (user?.email ? user.email.split("@")[0] : "") ||
        t("polls:member");
      const authenticatedEmail = user?.email || "";
      logger.info(
        "VOTING - Soumission directe (utilisateur connecté)",
        "vote",
        {
          authenticatedName,
          authenticatedEmail,
          userId: user?.id,
        },
      );
      try {
        await handleFinalizeVote({
          name: authenticatedName,
          email: authenticatedEmail,
        });
        return;
      } catch (error) {
        logger.error(
          "VOTING - Erreur finalisation vote utilisateur connecté, fallback sur formulaire",
          "vote",
          { error },
        );
        setShowForm(true);
        return;
      }
    }
    setShowForm(true);
  };

  return (
    <div
      data-testid="voting-component"
      className="flex flex-col min-h-screen-safe bg-background dark:bg-[#0a0a0a] pb-8"
    >
      {/* Header avec Logo - Simplifié */}
      <PollHeader
        poll={realPoll}
        totalVotes={displayOptions.length}
        totalVoters={totalVoters}
        remainingVotes={remainingVotes}
        progressPercent={progressPercent}
      />

      {/* Toggle vue swipe/table + Bouton résultats */}
      <div className="max-w-4xl mx-auto px-4 pt-4">
        <div className="flex flex-wrap justify-start items-center gap-2 sm:justify-between">
          {/* Bouton résultats si accessibles (publics, votants, ou créateur) */}
          {resultsButtonVisible && (
            <button
              onClick={() => navigate(`/poll/${slug}/results`)}
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors border border-primary"
              data-testid="view-results-button-header"
            >
              <BarChart3 className="w-4 h-4" />
              <span className="text-sm">{t("polls:view_results")}</span>
            </button>
          )}
          <button
            onClick={toggleViewMode}
            className="flex items-center gap-2 px-4 py-2 bg-accent dark:bg-[#2a2a2a] hover:bg-muted dark:hover:bg-[#353535] text-foreground rounded-lg transition-colors border border-border"
            data-testid="table-view-button"
          >
            {isTableMode ? (
              <>
                <LayoutGrid className="w-4 h-4" />
                <span className="text-sm">{t("polls:card_view")}</span>
              </>
            ) : (
              <>
                <Table className="w-4 h-4" />
                <span className="text-sm">{t("polls:table_view")}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Contenu principal - Options de vote */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="max-w-4xl mx-auto space-y-4">
          {/* Bannière résultats masqués (resultsVisibility voters/creator-only).
              Le filtrage réel est fait serveur par get_poll_responses ; cette
              bannière explique pourquoi les compteurs des autres sont absents. */}
          {resultsHidden && (
            <div
              className="flex items-center gap-2 bg-warning/10 border border-warning/30 rounded-lg px-4 py-3 text-warning text-sm"
              data-testid="results-hidden-banner"
            >
              <span aria-hidden>🙈</span>
              <span>
                {pollSettings.resultsVisibility === "creator-only"
                  ? t("polls:results_hidden_creator_only")
                  : t("polls:results_hidden_voters")}
              </span>
            </div>
          )}
          {/* Bannière sondage récurrent : contexte + bascule focus/toutes les dates */}
          {recurring && (
            <div
              className="flex flex-wrap items-center justify-between gap-2 bg-info/10 border border-info/30 rounded-lg px-4 py-3"
              data-testid="recurring-banner"
            >
              <div className="flex items-center gap-2 text-info text-sm">
                <Repeat className="w-4 h-4 shrink-0" />
                {recurringViewAll || !recurring.focusDate ? (
                  <span>
                    {t("polls:recurring.banner_all", {
                      count: recurring.total,
                    })}
                  </span>
                ) : (
                  <span>
                    {t("polls:recurring.banner_focus", {
                      date: formatDate(recurring.focusDate),
                      position: recurring.focusPosition,
                      total: recurring.total,
                    })}
                  </span>
                )}
              </div>
              {recurring.focusDate && (
                <button
                  onClick={() => setRecurringViewAll((v) => !v)}
                  className="text-sm text-info hover:text-info underline underline-offset-2"
                  data-testid="recurring-toggle-view"
                >
                  {recurringViewAll
                    ? t("polls:recurring.show_next_only")
                    : t("polls:recurring.show_all_dates", {
                        count: recurring.total,
                      })}
                </button>
              )}
            </div>
          )}

          {/* Mode tableau */}
          {isTableMode ? (
            <div data-testid="voting-table" className="mb-6">
              {/* Design original simple - Dark Theme */}
              <div className="bg-card dark:bg-[#1e1e1e] rounded-lg border border-border overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted border-b border-border">
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          {currentUserDisplayName || t("polls:you")}
                        </div>
                      </th>
                      {/* Headers pour chaque option */}
                      {displayOptions.map((option) => (
                        <th
                          key={option.id}
                          className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider border-l border-border"
                        >
                          <div className="space-y-1">
                            <div className="text-foreground">
                              {option.date_group_label ||
                                formatDate(option.option_date)}
                            </div>
                            {option.description && (
                              <div className="text-sm font-medium text-foreground">
                                {option.description}
                              </div>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-card dark:bg-[#1e1e1e] divide-y divide-border">
                    {/* Ligne de l'utilisateur actuel */}
                    <tr className="hover:bg-accent transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap bg-muted border-r border-border">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                            <span className="text-primary-foreground text-sm font-medium">
                              {currentUserDisplayName?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-foreground">
                            {currentUserDisplayName}
                          </span>
                        </div>
                      </td>

                      {/* Cellules de vote pour chaque option */}
                      {displayOptions.map((option) => {
                        const currentVote = votingHook.currentVote[option.id];
                        return (
                          <td
                            key={option.id}
                            className="px-4 py-3 text-center border-r border-border"
                          >
                            <div className="flex gap-1 justify-center">
                              <button
                                onClick={() => toggleVote(option.id, "yes")}
                                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                                  currentVote === "yes"
                                    ? "bg-success text-success-foreground"
                                    : "bg-muted text-muted-foreground hover:bg-accent"
                                }`}
                                data-testid={`vote-button-yes-${option.id}`}
                                aria-label={t("polls:available_on", {
                                  label: option.label,
                                })}
                                aria-pressed={currentVote === "yes"}
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => toggleVote(option.id, "maybe")}
                                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                                  currentVote === "maybe"
                                    ? "bg-warning text-warning-foreground"
                                    : "bg-muted text-muted-foreground hover:bg-accent"
                                }`}
                                data-testid={`vote-button-maybe-${option.id}`}
                                aria-label={t("polls:maybe_available_on", {
                                  label: option.label,
                                })}
                                aria-pressed={currentVote === "maybe"}
                              >
                                <HelpCircle className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => toggleVote(option.id, "no")}
                                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                                  currentVote === "no"
                                    ? "bg-destructive text-destructive-foreground"
                                    : "bg-muted text-muted-foreground hover:bg-accent"
                                }`}
                                data-testid={`vote-button-no-${option.id}`}
                                aria-label={t("polls:unavailable_on", {
                                  label: option.label,
                                })}
                                aria-pressed={currentVote === "no"}
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        );
                      })}
                    </tr>

                    {/* Lignes des autres votants (lecture seule) */}
                    {(() => {
                      const seen = new Set<string>();
                      // Exclure aussi le nom courant pour les cas legacy.
                      if (currentUserDisplayName) {
                        seen.add(currentUserDisplayName);
                      }
                      return allVotes
                        .filter((v) => !(v as any).is_own_response)
                        .map((v) => ({
                          name: resolveVoterName(
                            v as any,
                            t("polls:results_anonymous"),
                          ),
                          selections: Object.fromEntries(
                            Object.entries(
                              (v as any).response_data || {},
                            ).filter(([k]) => k !== "voterName"),
                          ) as Record<string, "yes" | "no" | "maybe">,
                        }))
                        .filter((p) => {
                          if (seen.has(p.name)) return false;
                          seen.add(p.name);
                          return true;
                        })
                        .map((p, idx) => (
                          <tr
                            key={`other-${idx}-${p.name}`}
                            className="hover:bg-accent transition-colors"
                          >
                            <td className="px-4 py-3 whitespace-nowrap bg-muted border-r border-border">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                                  <span className="text-secondary-foreground text-sm font-medium">
                                    {p.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <span className="text-sm font-medium text-foreground">
                                  {p.name}
                                </span>
                              </div>
                            </td>
                            {displayOptions.map((option) => {
                              const vote = p.selections[option.id];
                              const bg =
                                vote === "yes"
                                  ? "bg-success"
                                  : vote === "maybe"
                                    ? "bg-warning"
                                    : vote === "no"
                                      ? "bg-destructive"
                                      : "bg-muted";
                              return (
                                <td
                                  key={option.id}
                                  className="px-4 py-3 text-center border-l border-border"
                                >
                                  <div
                                    className={`w-8 h-8 mx-auto rounded-lg ${bg}`}
                                    title={
                                      vote === "yes"
                                        ? t("polls:available")
                                        : vote === "maybe"
                                          ? t("common:maybe")
                                          : vote === "no"
                                            ? t("polls:unavailable")
                                            : t("polls:no_response")
                                    }
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        ));
                    })()}
                  </tbody>
                  {/* Ligne des résultats globaux */}
                  <tfoot>
                    <tr className="bg-muted border-t border-border">
                      <td className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="h-4 w-4" />
                          {t("polls:results.title")}
                        </div>
                      </td>
                      {/* Stats pour chaque option */}
                      {displayOptions.map((option) => {
                        const stats = getStatsWithUser(option.id);
                        const yesCount = stats.yes;
                        const noCount = stats.no;
                        const maybeCount = stats.maybe;
                        const totalCount = yesCount + noCount + maybeCount;

                        return (
                          <td
                            key={option.id}
                            className="px-4 py-3 text-center border-l border-border"
                          >
                            <div className="flex gap-1 justify-center items-center">
                              <div className="flex items-center gap-1">
                                <div
                                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                                    yesCount > 0
                                      ? "bg-success text-success-foreground"
                                      : "bg-muted text-muted-foreground"
                                  }`}
                                >
                                  {yesCount}
                                </div>
                                <div
                                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                                    maybeCount > 0
                                      ? "bg-warning text-warning-foreground"
                                      : "bg-muted text-muted-foreground"
                                  }`}
                                >
                                  {maybeCount}
                                </div>
                                <div
                                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                                    noCount > 0
                                      ? "bg-destructive text-destructive-foreground"
                                      : "bg-muted text-muted-foreground"
                                  }`}
                                >
                                  {noCount}
                                </div>
                              </div>
                              {totalCount > 0 && (
                                <div className="text-xs text-muted-foreground ml-2">
                                  ({totalCount})
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ) : redesignV2 ? (
            /* Redesign V2 — liste compacte SlotVoteRow (flag) */
            <div
              className="bg-card dark:bg-[#1e1e1e] rounded-lg border border-border overflow-hidden"
              data-testid="voting-compact-list"
            >
              {displayOptions.map((option, index) => {
                const prev = index > 0 ? displayOptions[index - 1] : null;
                const showDayBanner =
                  !prev ||
                  (prev.date_group_label || prev.option_date) !==
                    (option.date_group_label || option.option_date);
                const current = (votingHook.currentVote || {})[option.id] as
                  | VoteType
                  | undefined;
                return (
                  <SlotVoteRow
                    key={option.id}
                    option={option}
                    userVote={current ?? null}
                    showDayBanner={showDayBanner}
                    allowMaybeVotes={allowMaybeVotes}
                    onVote={(optionId, voteType) => {
                      // SlotVoteRow signale null sur reclic same value
                      // (toggle clear). Câbler removeVote pour matcher la
                      // sémantique demandée par le handoff.
                      if (voteType) {
                        handleVote(optionId, voteType);
                      } else if (votingHook && votingHook.removeVote) {
                        votingHook.removeVote(optionId);
                      }
                    }}
                  />
                );
              })}
            </div>
          ) : (
            /* Mode swipe/cartes - Mobile et Desktop */
            displayOptions.map((option, index) => (
              <div
                key={option.id}
                className="bg-card dark:bg-[#1e1e1e] rounded-lg border border-border overflow-hidden"
              >
                {/* Voting Area - Pas de header, juste les boutons de vote */}
                <div className="p-4">
                  {isDesktop ? (
                    <VoteOptionDesktop
                      option={option}
                      index={index}
                      userVote={
                        (votingHook.currentVote || {})[option.id] || "maybe"
                      }
                      userHasVoted={!!(votingHook.currentVote || {})[option.id]}
                      anyUserHasVoted={allVotes.length > 0}
                      handleVote={handleVote}
                      getStatsWithUser={getStatsWithUser}
                      getExistingStats={getExistingStats}
                      getRanking={getRanking}
                      totalVoters={totalVoters}
                      allowMaybeVotes={allowMaybeVotes}
                    />
                  ) : (
                    <VoteOptionMobile
                      option={option}
                      index={index}
                      userVote={
                        (votingHook.currentVote || {})[option.id] || "maybe"
                      }
                      userHasVoted={!!(votingHook.currentVote || {})[option.id]}
                      anyUserHasVoted={allVotes.length > 0}
                      handleVote={handleVote}
                      getStatsWithUser={getStatsWithUser}
                      getExistingStats={getExistingStats}
                      getRanking={getRanking}
                      totalVoters={totalVoters}
                      allowMaybeVotes={allowMaybeVotes}
                    />
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Interface de vote - Nouveau vote ou modification */}
        {!showForm && (!votingHook.hasVoted || !showVotedMessage) && (
          <div
            className={`max-w-4xl mx-auto mt-6 px-4 flex flex-col items-center pb-20 ${
              redesignV2
                ? "sticky bottom-0 bg-gradient-to-t from-background via-background/95 to-transparent pt-4"
                : ""
            }`}
          >
            <button
              className="w-64 py-4 rounded-xl font-bold text-lg transition-all bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-primary/20 active:scale-95 translate-y-0 hover:-translate-y-1"
              style={
                realPoll?.template
                  ? {
                      backgroundColor: realPoll.template.primary_color,
                      color: readableTextColor(realPoll.template.primary_color),
                    }
                  : undefined
              }
              onClick={handleSubmitClick}
              data-testid="submit-votes-button"
            >
              {t("polls:submit_my_votes")}
            </button>
            {!user && (
              <p className="text-xs text-muted-foreground text-center mt-3 max-w-md">
                {t("polls:anonymous_identifier_notice")}{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-foreground"
                >
                  {t("polls:learn_more")}
                </a>
              </p>
            )}
          </div>
        )}

        {/* Message de reconnaissance pour utilisateur déjà voté */}
        {votingHook.hasVoted && showVotedMessage && (
          <div className="max-w-4xl mx-auto mt-6 px-4 flex justify-center pb-20">
            <div className="bg-card border border-border rounded-lg p-6 text-center max-w-md">
              <div className="text-success font-medium text-lg mb-2">
                {t("polls:welcome_already_voted", {
                  name: currentUserDisplayName,
                })}
              </div>
              <div className="text-foreground text-sm">
                {editLocked
                  ? t("polls:vote_locked_message")
                  : t("polls:can_edit_or_delete_vote")}
              </div>
              {!editLocked && (
                <>
                  <button
                    data-testid="edit-vote-btn"
                    className="mt-4 w-full py-2 px-4 bg-muted hover:bg-accent text-muted-foreground rounded-lg transition-colors"
                    onClick={() => {
                      setShowVotedMessage(false);
                      setShowForm(false);
                    }}
                  >
                    {t("polls:edit_my_vote")}
                  </button>
                  <button
                    data-testid="delete-own-vote-btn"
                    className="mt-2 w-full py-2 px-4 bg-transparent hover:bg-destructive-bg text-destructive hover:text-destructive border border-destructive rounded-lg transition-colors flex items-center justify-center gap-2"
                    onClick={() => setShowSelfDeleteDialog(true)}
                  >
                    <Trash2 className="h-4 w-4" />
                    {t("polls:delete_my_vote")}
                  </button>
                </>
              )}
              {offerFinalDateSubscribe && realPoll?.id && (
                <FinalDateSubscribe
                  pollId={realPoll.id}
                  defaultEmail={submittedEmail || voterEmail || undefined}
                  className="mt-4"
                />
              )}
            </div>
          </div>
        )}

        {/* Modal suppression du propre vote */}
        <AlertDialog
          open={showSelfDeleteDialog}
          onOpenChange={setShowSelfDeleteDialog}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("polls:delete_your_vote_question")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("polls:delete_vote_confirmation")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeletingOwnVote}>
                {t("common:cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-self-delete"
                onClick={handleSelfDelete}
                disabled={isDeletingOwnVote}
                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              >
                {isDeletingOwnVote ? t("polls:deleting") : t("common:delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Formulaire d'identification final */}
      {showForm && (
        <VoterForm
          options={displayOptions}
          votes={votingHook.currentVote || {}}
          userHasVoted={hasAlreadyVoted ? {} : {}}
          onBack={() => setShowForm(false)}
          onCancel={() => setShowForm(false)}
          voterInfo={{
            name:
              currentUserDisplayName === t("polls:you")
                ? ""
                : currentUserDisplayName,
            email: "",
          }}
          // 🔄 MIGRATION: VoterInfo n'est plus nécessaire avec le nouveau hook
          // L'identité est gérée automatiquement par fingerprint
          // voterInfo={voterEmail ? { name: "", email: voterEmail } : undefined} // ❌ ARCHIVED
          onSubmit={handleFinalizeVote}
          isSubmitting={false}
          getVoteText={(v: VoteType) =>
            v === "yes"
              ? t("common:yes")
              : v === "maybe"
                ? t("common:maybe")
                : t("common:no")
          }
          // L'email n'est exigé que si le sondage est sur invitation ET que le
          // serveur n'a pas déjà identifié l'invité (jeton valide). Auparavant
          // ce champ était piloté par resultsVisibility, qui ne parle pas du
          // vote mais de la visibilité des résultats.
          isRestricted={isInviteOnly && !inviteEmailMasked}
        />
      )}
    </div>
  );
};

const handleBack = () => {
  window.history.back();
};

export default Voting;
